import { Grid, MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import * as FountainApi from '~/api/FountainApi';
import ChowbusButton from '~/components/buttons/Button.js';
import ChowbusForm from '~/components/ChowbusForm';
import InternationalPhoneInput from '~/components/ChowbusForm/InternationalPhoneInput';
import { mixpanel } from '~/third_party/Mixpanel.jsx';

const CountryAbbrMap = {
  'United States': 'US',
  Canada: 'CA',
};

const CITIES = [
  '',
  'Ann Arbor, MI',
  'Atlanta, GA',
  'Austin, TX',
  'Boston, MA',
  'Chicago, IL area',
  'Columbus, OH',
  'Houston, TX',
  'Lansing, MI',
  'Los Angeles area, CA',
  'Madison, WI',
  'Minneapolis, MN',
  'New York City area, NY',
  'Edison, NJ',
  'Philadelphia, PA',
  'Rockville, MD',
  'San Diego, CA',
  'San Francisco, CA',
  'Seattle area, WA',
  'Silicon Valley area, CA',
  'St. Louis, MO',
  'Syracuse, NY',
  'West Lafayette, IN',
  'Toronto, Canada',
  'Vancouver, Canada',
  'Dallas-Forth Worth, TX',
  'Cincinnati, OH',
  'Raleigh-Durham-Chapel Hill, NC',
  'Bloomington, IN',
  'Portland, OR',
];

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'proximanova-bold',
    marginBottom: 20,
  },
  form: {
    maxWidth: 710,
    margin: '0 auto',
  },
  formAction: {
    textAlign: 'center',
    marginTop: 32,
  },
}));

function DriverForm() {
  const classes = useStyles();
  const [interacted, setInteracted] = useState(false);
  const [formError, setFormError] = useState('');

  const onFormChange = () => {
    if (interacted) {
      return;
    }
    setInteracted(true);
    mixpanel.track({ name: 'user started driver page lead form', properties: {} });
  };

  const onFormSubmit = async (values) => {
    setFormError('');
    try {
      const driver = {
        name: `${values.firstName} ${values.lastName}`,
        email: values.email,
        phone_number: values.phoneNumber.number,
        data: {
          country_code: 'US',
          city_selection: values.city_selection,
          ccpa_accepted: false,
          utm_source: 'chowbuswebsite',
        },
      };
      const { redirectUrl } = await FountainApi.sendDriverApplication(driver);
      mixpanel.track({ name: 'driver page lead form submitted', properties: {} });
      if (redirectUrl) {
        window.location.assign(redirectUrl);
      }
    } catch (e) {
      setFormError('There was a problem submitting your application. Please try again.');
      Sentry.captureException(e);
    }
  };

  return (
    <>
      <Typography variant='h4' align='center' component='h1' className={classes.title}>
        Sign Up To Deliver Today
      </Typography>
      <ChowbusForm.Form onSubmit={onFormSubmit} onChange={onFormChange} className={classes.form}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ChowbusForm.Field
              name='firstName'
              required
              validate={ChowbusForm.validators.lengthValidator({ max: 80, min: 2 })}>
              <ChowbusForm.Input
                type='text'
                fullWidth
                label='First Name *'
                id='firstName'
                name='firstName'
              />
            </ChowbusForm.Field>
          </Grid>

          <Grid item xs={12}>
            <ChowbusForm.Field
              name='lastName'
              required
              validate={ChowbusForm.validators.lengthValidator({ max: 80, min: 2 })}>
              <ChowbusForm.Input
                type='text'
                fullWidth
                label='Last Name *'
                id='lastName'
                name='lastName'
              />
            </ChowbusForm.Field>
          </Grid>

          <Grid item xs={12}>
            <ChowbusForm.Field
              name='email'
              required
              validate={[
                ChowbusForm.validators.emailValidator,
                ChowbusForm.validators.lengthValidator({ max: 80, min: 2 }),
              ]}>
              <ChowbusForm.Input type='text' fullWidth label='Email *' id='email' name='email' />
            </ChowbusForm.Field>
          </Grid>

          <Grid item xs={12}>
            <ChowbusForm.Field
              name='phoneNumber'
              required
              validate={ChowbusForm.validators.i18nPhoneNumberValidator}>
              {(fieldContext) => (
                <InternationalPhoneInput
                  country={CountryAbbrMap['US']}
                  error={fieldContext.error}
                  errorMessage={fieldContext.errorMessage}
                  onChange={fieldContext.onChange}
                  variant='signup'
                />
              )}
            </ChowbusForm.Field>
          </Grid>

          <Grid item xs={12}>
            <ChowbusForm.Field
              name='city_selection'
              required
              validate={ChowbusForm.validators.lengthValidator({ min: 1 })}>
              {(fieldContext) => (
                <ChowbusForm.Select
                  label='Which city do you want to work in? 您希望在哪座城市工作？ *'
                  name={fieldContext.name}
                  value={fieldContext.fields[fieldContext.name]}
                  onChange={fieldContext.onChange}
                  fullWidth>
                  {CITIES.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </ChowbusForm.Select>
              )}
            </ChowbusForm.Field>
          </Grid>
          {formError.length ? (
            <Grid item xs={12}>
              <Alert severity='error'>{formError}</Alert>
            </Grid>
          ) : null}
          <Grid item xs={12} className={classes.formAction}>
            <ChowbusForm.Field>
              {(fieldContext) => (
                <ChowbusButton
                  type='submit'
                  color='primary'
                  variant='contained'
                  disabled={!fieldContext.isValid}>
                  Click Here to Apply
                </ChowbusButton>
              )}
            </ChowbusForm.Field>
          </Grid>
        </Grid>
      </ChowbusForm.Form>
    </>
  );
}

export default DriverForm;
